import {
  addMoney,
  type ExchangeRequest,
  exchangeRequestSchema,
  type ForeignExpenseRequest,
  foreignExpenseRequestSchema,
  isNegativeMoney,
  isZeroMoney,
  parseAmountInput,
  parseRateInput,
  subtractMoney,
  type Transaction,
} from '@familywallet/shared';
import { z } from 'zod';
import { parsePositive } from './add-form';
import { groupAmountInput } from './money';

/**
 * The forms for money in two currencies (Phase 4 PR 3): Exchange, and an expense paid in
 * another currency. Values are kept as typed; the checks return i18n keys per field, and the
 * request is checked by the same shared schema the API uses.
 */

interface Issue {
  path: (string | number)[];
  message: string;
}

const amountIssue = (raw: string, currency: string) =>
  raw.trim() === ''
    ? 'add.errors.amountRequired'
    : currency === 'LBP' && parsePositive(raw, 'USD')
      ? 'add.errors.amountWhole'
      : 'add.errors.amount';

function formSchema<V, R>(check: (v: V) => Issue[], toRequest: (v: V) => R) {
  return z
    .custom<V>()
    .superRefine((v, c) => {
      for (const issue of check(v)) {
        c.addIssue({ code: 'custom', path: issue.path, message: issue.message });
      }
    })
    .transform(toRequest);
}

// ---------------------------------------------------------------------------------------------
// Exchange
// ---------------------------------------------------------------------------------------------

export interface ExchangeFormValues {
  fromAccountId: string;
  fromCurrency: string;
  fromAmount: string;
  toAccountId: string;
  toCurrency: string;
  toAmount: string;
  occurredOn: string;
  description: string;
}

export function checkExchangeForm(v: ExchangeFormValues): Issue[] {
  const issues: Issue[] = [];
  if (!parsePositive(v.fromAmount, v.fromCurrency)) {
    issues.push({ path: ['fromAmount'], message: amountIssue(v.fromAmount, v.fromCurrency) });
  }
  if (!parsePositive(v.toAmount, v.toCurrency)) {
    issues.push({ path: ['toAmount'], message: amountIssue(v.toAmount, v.toCurrency) });
  }
  if (!v.fromAccountId) issues.push({ path: ['fromAccountId'], message: 'add.errors.account' });
  if (!v.toAccountId) issues.push({ path: ['toAccountId'], message: 'add.errors.account' });
  if (v.fromCurrency === v.toCurrency) {
    issues.push({ path: ['toCurrency'], message: 'exchange.errors.sameCurrency' });
  }
  if (!v.occurredOn) issues.push({ path: ['occurredOn'], message: 'add.errors.date' });
  return issues;
}

export function toExchangeRequest(v: ExchangeFormValues): ExchangeRequest {
  return exchangeRequestSchema.parse({
    fromAccountId: v.fromAccountId,
    fromCurrency: v.fromCurrency,
    fromAmount: parsePositive(v.fromAmount, v.fromCurrency),
    toAccountId: v.toAccountId,
    toCurrency: v.toCurrency,
    toAmount: parsePositive(v.toAmount, v.toCurrency),
    occurredOn: v.occurredOn,
    ...(v.description.trim() ? { description: v.description.trim() } : {}),
  }) as ExchangeRequest;
}

export const exchangeFormSchema = formSchema(checkExchangeForm, toExchangeRequest);

// ---------------------------------------------------------------------------------------------
// Expense (or income) paid with several payments, in one or more currencies and accounts
// ---------------------------------------------------------------------------------------------

/** One payment (or change) line as typed: an amount, its currency and its account. */
export interface PaymentLineValues {
  accountId: string;
  amount: string;
  currency: string;
}

export interface ForeignExpenseFormValues {
  kind: 'expense' | 'income';
  categoryId: string;
  billAmount: string;
  billCurrency: string;
  /** "Paid now" (for an income: received), one line per payment. */
  payments: PaymentLineValues[];
  /** The change, in any family currency, into any account. */
  returns: PaymentLineValues[];
  payeeName: string;
  occurredOn: string;
  description: string;
  sharedWithFamily: boolean;
  rate: string;
  /** Split into several categories, in the expense's currency. */
  split: boolean;
  lines: { categoryId: string; amount: string; note: string }[];
  /** Part on credit: whatever the payments leave unpaid of the bill is owed. */
  credit: boolean;
  personId: string;
  personName: string;
  dueOn: string;
}

/**
 * The currency of what was spent: the bill's; with no bill, the paid currency when every
 * payment is in it, else the family currency (the estimate's).
 */
export function expenseCurrencyOf(v: ForeignExpenseFormValues, baseCurrency: string): string {
  if (v.billAmount.trim() !== '') return v.billCurrency;
  const paid = new Set(v.payments.map((p) => p.currency));
  return paid.size === 1 ? [...paid][0]! : baseCurrency;
}

/**
 * For a split: what was spent minus the lines typed so far. Null when a typed line is not a
 * valid amount.
 */
export function foreignSplitRemaining(
  lines: ForeignExpenseFormValues['lines'],
  spent: string,
  currency: string,
): string | null {
  const typed = lines
    .filter((l) => l.amount.trim() !== '')
    .map((l) => parsePositive(l.amount, currency));
  if (typed.some((l) => l === null)) return null;
  return subtractMoney(spent, addMoney('0', ...(typed as string[])));
}

/** Returned as typed: empty is none; zero is allowed. Null when it is not a valid amount. */
export function parseReturned(raw: string, currency: string): string | null {
  if (raw.trim() === '') return '0';
  const amount = parseAmountInput(raw, currency);
  return amount && !isNegativeMoney(amount) ? amount : null;
}

/** The bill as typed: null when empty (then it is estimated), undefined when not valid. */
export function parseBill(raw: string, currency: string): string | null | undefined {
  if (raw.trim() === '') return null;
  return parsePositive(raw, currency) ?? undefined;
}

/** The payment and change lines, parsed; null when any typed one is not valid yet. */
export function parsedPaymentLines(v: ForeignExpenseFormValues): {
  paid: { accountId: string; amount: string; currency: string }[];
  returned: { accountId: string; amount: string; currency: string }[];
} | null {
  const paid = v.payments.map((p) => ({ ...p, amount: parsePositive(p.amount, p.currency) }));
  const returned = v.returns.map((r) => ({ ...r, amount: parseReturned(r.amount, r.currency) }));
  if (paid.some((p) => p.amount === null) || returned.some((r) => r.amount === null)) return null;
  return {
    paid: paid as { accountId: string; amount: string; currency: string }[],
    returned: (returned as { accountId: string; amount: string; currency: string }[]).filter(
      (r) => !isZeroMoney(r.amount),
    ),
  };
}

export function checkForeignExpenseForm(
  v: ForeignExpenseFormValues,
  askRate: boolean,
  baseCurrency: string,
): Issue[] {
  const issues: Issue[] = [];
  v.payments.forEach((p, i) => {
    if (!p.accountId) {
      issues.push({ path: ['payments', i, 'accountId'], message: 'add.errors.account' });
    } else if (!parsePositive(p.amount, p.currency)) {
      issues.push({ path: ['payments', i, 'amount'], message: amountIssue(p.amount, p.currency) });
    }
  });
  v.returns.forEach((r, i) => {
    if (parseReturned(r.amount, r.currency) === null) {
      issues.push({ path: ['returns', i, 'amount'], message: amountIssue(r.amount, r.currency) });
    } else if (r.amount.trim() !== '' && !r.accountId) {
      issues.push({ path: ['returns', i, 'accountId'], message: 'add.errors.account' });
    }
  });
  const bill = parseBill(v.billAmount, v.billCurrency);
  if (bill === undefined) {
    issues.push({ path: ['billAmount'], message: amountIssue(v.billAmount, v.billCurrency) });
  }
  if (v.split) {
    const currency = expenseCurrencyOf(v, baseCurrency);
    v.lines.forEach((l, i) => {
      if (!l.categoryId) {
        issues.push({ path: ['lines', i, 'categoryId'], message: 'add.errors.category' });
      } else if (!parsePositive(l.amount, currency)) {
        issues.push({ path: ['lines', i, 'amount'], message: amountIssue(l.amount, currency) });
      }
    });
  } else if (!v.categoryId) {
    issues.push({ path: ['categoryId'], message: 'add.errors.category' });
  }
  if (v.credit) {
    if (bill === null)
      issues.push({ path: ['billAmount'], message: 'foreign.errors.billForCredit' });
    if (!v.personId && !v.personName) {
      issues.push({ path: ['personId'], message: 'credit.errors.person' });
    }
  }
  if (!v.occurredOn) issues.push({ path: ['occurredOn'], message: 'add.errors.date' });
  if (askRate && !parseRateInput(v.rate)) {
    issues.push({ path: ['rate'], message: 'add.errors.rate' });
  }
  return issues;
}

export function toForeignExpenseRequest(
  v: ForeignExpenseFormValues,
  askRate: boolean,
  baseCurrency: string,
): ForeignExpenseRequest {
  const bill = parseBill(v.billAmount, v.billCurrency);
  const { paid, returned } = parsedPaymentLines(v)!;
  return foreignExpenseRequestSchema.parse({
    kind: v.kind,
    ...(v.split
      ? {
          lines: v.lines.map((l) => ({
            categoryId: l.categoryId,
            amount: parsePositive(l.amount, expenseCurrencyOf(v, baseCurrency)),
            ...(l.note.trim() ? { note: l.note.trim() } : {}),
          })),
        }
      : { categoryId: v.categoryId }),
    ...(bill ? { bill: { amount: bill, currency: v.billCurrency } } : {}),
    payments: paid,
    returns: returned,
    ...(v.credit
      ? {
          credit: {
            ...(v.personId ? { personId: v.personId } : { personName: v.personName }),
            ...(v.dueOn ? { dueOn: v.dueOn } : {}),
          },
        }
      : {}),
    ...(askRate && !bill ? { rate: parseRateInput(v.rate)! } : {}),
    ...(v.payeeName.trim() ? { payeeName: v.payeeName.trim() } : {}),
    occurredOn: v.occurredOn,
    ...(v.description.trim() ? { description: v.description.trim() } : {}),
    sharedWithFamily: v.sharedWithFamily,
  }) as ForeignExpenseRequest;
}

export function foreignExpenseFormSchema(askRate: boolean, baseCurrency: string) {
  return formSchema(
    (v: ForeignExpenseFormValues) => checkForeignExpenseForm(v, askRate, baseCurrency),
    (v: ForeignExpenseFormValues) => toForeignExpenseRequest(v, askRate, baseCurrency),
  );
}

/**
 * A transaction paid in several payments, currencies or accounts: Correct opens it in this
 * form. True when it has more than one money line or more than one currency.
 */
export function isSplitPayment(tx: Transaction): boolean {
  if (tx.kind !== 'expense' && tx.kind !== 'income') return false;
  const money = tx.lines.filter((l) => l.role === 'money');
  return money.length > 1 || Object.keys(tx.rates).length > 1;
}

/** Correct: what such a transaction fills the form with (amounts as typed, positive). */
export function fromSplitTransaction(tx: Transaction): {
  values: Partial<ForeignExpenseFormValues>;
  personLabel: string;
} {
  const out = tx.kind === 'expense' ? -1 : 1;
  const positive = (v: string, currency: string) =>
    groupAmountInput(v.startsWith('-') ? v.slice(1) : v, currency);
  const money = tx.lines.filter((l) => l.role === 'money' && l.accountId && l.amount !== null);
  const toLine = (l: (typeof money)[number]) => ({
    accountId: l.accountId!,
    amount: positive(l.amount!, l.currency),
    currency: l.currency,
  });
  const isPayment = (l: (typeof money)[number]) => isNegativeMoney(l.amount!) === (out === -1);
  const categories = tx.lines.filter((l) => l.role === 'category' && l.accountId && l.amount);
  const split = categories.length > 1;
  const billCurrency = categories[0]?.currency ?? tx.currency;
  return {
    personLabel: tx.credit?.personName ?? '',
    values: {
      kind: tx.kind as 'expense' | 'income',
      billAmount: tx.estimatedWithFamilyRate || !tx.amount ? '' : positive(tx.amount, billCurrency),
      billCurrency,
      payments: money.filter(isPayment).map(toLine),
      returns: money.filter((l) => !isPayment(l)).map(toLine),
      categoryId: split ? '' : (categories[0]?.accountId ?? ''),
      split,
      lines: split
        ? categories.map((l) => ({
            categoryId: l.accountId!,
            amount: positive(l.amount!, l.currency),
            note: l.note ?? '',
          }))
        : [],
      payeeName: tx.payee?.name ?? '',
      occurredOn: tx.occurredOn,
      description: tx.description,
      sharedWithFamily: tx.sharedWithFamily,
      ...(tx.credit
        ? { credit: true, personId: tx.credit.personId, dueOn: tx.credit.dueOn ?? '' }
        : {}),
    },
  };
}
